<template>
  <b-sidebar
    right
    shadow
    backdrop
    width="min(420px, 100vw)"
    :visible="visible"
    :title="file ? file.name : ''"
    data-test="project-file-drawer"
    @change="onChange"
    @hidden="$emit('hidden')"
  >
    <div v-if="file" class="drawer-body">
      <component
        :is="canOpenFull ? 'button' : 'div'"
        :type="canOpenFull ? 'button' : null"
        :class="['drawer-preview', { 'drawer-preview-link': canOpenFull }]"
        :aria-label="canOpenFull ? 'View full size' : null"
        data-test="drawer-preview"
        @click="canOpenFull && (previewOpen = true)"
      >
        <img
          v-if="kind === 'img' && preview.state === 'ready' && !imageFailed"
          :src="preview.url"
          :alt="file.name"
          class="drawer-image"
          data-test="drawer-image"
          @error="imageFailed = true"
        />
        <file-thumb-preview
          v-else-if="canThumb"
          :file="thumbFile"
          :kind="kind"
          :content-path="contentUrl"
          @failed="thumbFailed = true"
        />
        <div v-else :class="['drawer-tile', `kind-${kind}`]">
          {{ typeLabel }}
        </div>
      </component>

      <dl class="drawer-details">
        <dt>Type</dt>
        <dd data-test="drawer-type">{{ typeLabel }}</dd>
        <dt>Size</dt>
        <dd data-test="drawer-size">{{ sizeLabel }}</dd>
        <template v-if="file.expires_at">
          <dt>Expiry</dt>
          <dd data-test="drawer-expiry" :title="expiryText">
            {{ expiryDate }}
            <span
              v-if="level"
              :class="['drawer-expiry', `expiry-${level}`]"
              data-test="drawer-expiry-pill"
            >
              {{ level === 'expired' ? 'Expired' : 'Expiring soon' }}
            </span>
          </dd>
        </template>
        <template v-if="file.tags && file.tags.length">
          <dt>Tags</dt>
          <dd data-test="drawer-tags">
            <button
              v-for="tag in file.tags"
              :key="tag"
              type="button"
              class="drawer-tag"
              :title="`Show files tagged ${tag}`"
              data-test="drawer-tag"
              @click="$emit('filter-tag', tag)"
            >
              {{ tag }}
            </button>
          </dd>
        </template>
        <dt>Uploaded by</dt>
        <dd data-test="drawer-uploader">{{ uploaderName }}</dd>
        <dt>Added</dt>
        <dd data-test="drawer-added">{{ addedText }}</dd>
        <template v-if="isArchived">
          <dt>Status</dt>
          <dd data-test="drawer-status">Archived {{ archivedText }}</dd>
        </template>
      </dl>

      <div class="drawer-actions">
        <button
          v-if="canOpenFull"
          type="button"
          class="drawer-action drawer-action-primary"
          data-test="drawer-open"
          @click="previewOpen = true"
        >
          <b-icon icon="box-arrow-up-right" />
          Open file
        </button>
        <button
          v-if="canDownload"
          type="button"
          class="drawer-action"
          :disabled="downloading"
          data-test="drawer-download"
          @click="$emit('download', file)"
        >
          <b-icon icon="download" />
          Download
        </button>
        <button
          v-if="canEdit"
          type="button"
          class="drawer-action"
          data-test="drawer-edit"
          @click="$emit('edit', file)"
        >
          <b-icon icon="pencil-square" />
          Edit
        </button>
        <button
          v-if="canEdit && !isArchived"
          type="button"
          class="drawer-action"
          :disabled="busy"
          data-test="drawer-archive"
          @click="$emit('archive', file)"
        >
          <b-icon icon="archive" />
          Archive
        </button>
        <button
          v-if="canEdit && isArchived"
          type="button"
          class="drawer-action"
          :disabled="busy"
          data-test="drawer-restore"
          @click="$emit('restore', file)"
        >
          <b-icon icon="arrow-counterclockwise" />
          Unarchive
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="drawer-action drawer-action-danger"
          data-test="drawer-delete"
          @click="$emit('delete', file)"
        >
          <b-icon icon="trash" />
          Delete
        </button>
      </div>

      <file-preview-modal
        v-if="canOpenFull"
        :visible="previewOpen"
        :url="preview.state === 'ready' ? preview.url : ''"
        :content-url="canPreviewContent ? contentUrl : ''"
        :mime-type="file.mime_type"
        :name="file.file_name || file.name"
        :allow-download="canDownload"
        :busy="downloading"
        custom-download
        @download="$emit('download', file)"
        @close="previewOpen = false"
      />
    </div>
  </b-sidebar>
</template>

<script>
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import FileThumbPreview from '@/components/files/FileThumbPreview'
import FilePreviewModal from '@/components/modals/FilePreviewModal'
import {
  expiryDescription,
  fileExtension,
  fileTypeLabel,
  formatBytes,
} from '@/utils/projectFiles'

/**
 * A project file, opened from the Files page: the same side drawer as a company
 * file (a drawn preview, the details, then the actions) instead of the modal the
 * project's own page uses. It does no writes itself: it emits, and the panel runs
 * the (already permission-checked) flows, exactly as it does for the modal.
 */
export default {
  name: 'ProjectFileDrawer',

  components: { FileThumbPreview, FilePreviewModal },

  props: {
    file: { type: Object, default: null },
    projectId: { type: [Number, String], required: true },
    visible: { type: Boolean, default: false },
    // What this user may do — decided by the panel (permissions + read-only).
    canDownload: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    // Something is in flight for this file.
    downloading: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      // idle | loading | ready | none | error — the signed link for an image or a PDF.
      preview: { state: 'idle', url: null },
      previewOpen: false,
      imageFailed: false,
      thumbFailed: false,
    }
  },

  computed: {
    isArchived() {
      return !!(this.file && this.file.archived_at)
    },

    // 'img' | 'pdf' | 'doc' | 'xls' | '' — the list's file kinds, from the extension.
    kind() {
      const ext = this.file ? fileExtension(this.file.file_name) : ''
      if (ext === 'pdf') return 'pdf'
      if (ext === 'docx') return 'doc'
      if (ext === 'csv' || ext === 'xlsx') return 'xls'
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'img'
      return ''
    },

    typeLabel() {
      return this.file
        ? fileTypeLabel(this.file.mime_type, this.file.file_name)
        : ''
    },

    sizeLabel() {
      return this.file ? formatBytes(this.file.size) : ''
    },

    // Where a PDF, Word or spreadsheet is read from to draw it.
    contentUrl() {
      return this.file
        ? `projects/${this.projectId}/files/${this.file.id}/content`
        : ''
    },

    thumbFile() {
      return {
        id: this.file.id,
        file_name: this.file.file_name,
        mime_type: this.file.mime_type,
      }
    },

    canThumb() {
      return ['pdf', 'doc', 'xls'].includes(this.kind) && !this.thumbFailed
    },

    // Word and spreadsheets are read through the API; the full view needs no signed link.
    canPreviewContent() {
      return this.kind === 'doc' || this.kind === 'xls'
    },

    canOpenFull() {
      return (
        !!this.file &&
        (this.canPreviewContent ||
          (this.preview.state === 'ready' && !!this.preview.url))
      )
    },

    // 'expired' | 'expiring' | null. Archived files never raise an alarm.
    level() {
      if (!this.file || this.isArchived) return null
      if (this.file.expiry_status === 'expired') return 'expired'
      if (this.file.expiry_status === 'expiring') return 'expiring'
      return null
    },

    expiryDate() {
      return this.file && this.file.expires_at
        ? dayjs(this.file.expires_at).format('DD/MM/YYYY')
        : ''
    },

    expiryText() {
      return expiryDescription(this.file, { archived: this.isArchived })
    },

    uploaderName() {
      const by = this.file && this.file.uploaded_by
      return by && by.full_name ? by.full_name : '-'
    },

    addedText() {
      const at = this.file && this.file.created_at
      return at ? dayjs(at).format('DD/MM/YYYY h:mm A') : '-'
    },

    archivedText() {
      const at = this.file && this.file.archived_at
      return at ? dayjs(at).format('DD/MM/YYYY') : ''
    },

    // Changes when the drawer moves to another file, or that file's document is replaced.
    previewKey() {
      if (!this.visible || !this.file) return null
      return `${this.file.id}|${this.file.document_id || ''}`
    },
  },

  watch: {
    previewKey: {
      immediate: true,
      handler(key) {
        this.previewOpen = false
        this.imageFailed = false
        this.thumbFailed = false
        if (key === null) {
          this.request = (this.request || 0) + 1
          this.preview = { state: 'idle', url: null }
          return
        }
        if (this.kind === 'img' || this.kind === 'pdf') this.loadPreview()
        else this.preview = { state: 'idle', url: null }
      },
    },
  },

  methods: {
    ...mapActions('projectFile', ['getPreview']),

    onChange(open) {
      if (!open) this.$emit('close')
    },

    async loadPreview() {
      // A slow answer for a file we've since moved off must not overwrite the current one.
      const request = (this.request = (this.request || 0) + 1)
      this.preview = { state: 'loading', url: null }
      try {
        const data = await this.getPreview({
          projectId: this.projectId,
          id: this.file.id,
        })
        if (request !== this.request) return
        this.preview =
          data && data.url
            ? { state: 'ready', url: data.url }
            : { state: 'none', url: null }
      } catch (error) {
        if (request !== this.request) return
        this.preview = { state: 'error', url: null }
      }
    },
  },
}
</script>

<style scoped>
.drawer-body {
  padding: 4px 20px 24px;
}

.drawer-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.drawer-preview-link {
  cursor: zoom-in;
}

.drawer-preview-link:hover {
  border-color: #006cff;
}

.drawer-image {
  max-width: 100%;
  max-height: 320px;
  border-radius: 6px;
}

.drawer-tile {
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  border-radius: 14px;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 18px;
  font-weight: 900;
}

.kind-pdf {
  background-color: #fdecec;
  color: #b42318;
}

.kind-doc {
  background-color: #e8f1ff;
  color: #0b55c9;
}

.kind-xls {
  background-color: #e6f6ec;
  color: #14703a;
}

.kind-img {
  background-color: #f1ebfb;
  color: #6b3fb0;
}

.drawer-details {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 8px 12px;
  margin-bottom: 24px;
  font-size: 14px;
}

.drawer-details dt {
  color: #6b7280;
  font-weight: 700;
}

.drawer-details dd {
  margin: 0;
  color: #111827;
  overflow-wrap: anywhere;
}

.drawer-expiry {
  margin-left: 6px;
  padding: 1px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.expiry-expired {
  background-color: #fdecec;
  color: #b42318;
}

.expiry-expiring {
  background-color: #fff4e0;
  color: #9a5b00;
}

/* The same tag chips as the project's file list. */
.drawer-tag {
  display: inline-block;
  margin: 0 4px 4px 0;
  padding: 0 0.6rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #374151;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.7;
}

.drawer-tag:hover {
  border-color: #2c66d5;
}

.drawer-actions {
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
}

.drawer-action-primary,
.drawer-action-primary svg {
  color: #006cff;
}

.drawer-action {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #374151;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
}

.drawer-action svg {
  margin-right: 12px;
  color: #6b7280;
}

.drawer-action:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.drawer-action:disabled {
  opacity: 0.6;
}

.drawer-action-danger,
.drawer-action-danger svg {
  color: #b42318;
}

.drawer-action-danger:hover:not(:disabled) {
  background-color: #fdecec;
}
</style>
